import React, { useRef } from "react";
import "./OtpDigitInput.css";

const OTP_LENGTH = 6;

/**
 * Six single-digit OTP boxes with paste and backspace focus movement.
 * Used by employee login and landing password-reset so the control stays identical.
 */
export default function OtpDigitInput({
  value = "",
  onChange,
  disabled = false,
  testIdPrefix = "otp-digit-input",
}) {
  const inputRefs = useRef([]);

  const handleChange = (event, index) => {
    const val = event.target.value;
    if (!/^\d*$/.test(val)) return;

    const next = value.split("");
    next[index] = val.slice(-1);
    const otpString = next.join("").slice(0, OTP_LENGTH);
    onChange(otpString);

    if (val && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !event.target.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedData) return;
    const otpString = pastedData.slice(0, OTP_LENGTH);
    onChange(otpString);
    const focusIndex = Math.min(otpString.length, OTP_LENGTH) - 1;
    inputRefs.current[Math.max(focusIndex, 0)]?.focus();
  };

  return (
    <div className="otp-digit-row">
      {[...Array(OTP_LENGTH)].map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ""}
          onChange={(event) => handleChange(event, index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          onPaste={handlePaste}
          disabled={disabled}
          className="otp-digit-box"
          autoComplete="one-time-code"
          aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
          data-testid={`${testIdPrefix}-${index}`}
        />
      ))}
    </div>
  );
}
